import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banner,
  BarChart,
  Button,
  EmptyState,
  Panel,
  PanelGrid,
  RankList,
  Segmented,
  SimpleTable,
  Spinner,
  StatGrid,
  StatTile,
  TextInput,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type SalesOverview } from "../../api/salesApi";
import { ScreenHeader } from "../page/ScreenHeader";
import { useShell } from "../shell/ShellContext";
import { CHANNEL_LABEL, errorMessage, fmtDate, fmtIls, fmtNum, monthLabel, periodRange, todayIso, type QuickPeriod } from "./salesUi";
import { useSalesMeta } from "./useSalesMeta";
import "./sales.css";

type Period = QuickPeriod | "custom";
type RecentDeal = SalesOverview["recentDeals"][number];

/** a CSS bar list — share of the largest value */
function Bars({ items, tone }: { items: Array<{ key: string; label: string; value: number; text: string }>; tone?: "success" }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (!items.length) return <p className="sl-muted">אין נתונים לתקופה</p>;
  return (
    <ul className="sl-bars">
      {items.map((i) => (
        <li key={i.key}>
          <span>{i.label}</span>
          <span className="sl-bars__track">
            <span className={`sl-bars__fill${tone ? ` sl-bars__fill--${tone}` : ""}`} style={{ width: `${Math.round((i.value / max) * 100)}%`, display: "block" }} />
          </span>
          <span className="sl-num">{i.text}</span>
        </li>
      ))}
    </ul>
  );
}

/** /sales — «לוח מכירות»: the period at a glance, who to call today, trends and breakdowns. */
export function SalesHomePage() {
  const navigate = useNavigate();
  const { allowed } = useShell();
  const { label } = useSalesMeta();
  const [period, setPeriod] = useState<Period>("30d");
  const [custom, setCustom] = useState(() => periodRange("30d"));
  const [data, setData] = useState<SalesOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trend, setTrend] = useState<"sales" | "leads">("sales");

  const range = useMemo(() => (period === "custom" ? custom : periodRange(period)), [period, custom]);

  useEffect(() => {
    let alive = true;
    setData(null);
    salesApi
      .overview(range)
      .then((d) => {
        if (!alive) return;
        setData(d);
        setError(null);
      })
      .catch((e) => alive && setError(errorMessage(e, "לא הצלחנו לטעון את לוח המכירות")));
    return () => {
      alive = false;
    };
  }, [range]);

  const today = todayIso();
  const loading = !data && !error;
  const empty = data && !data.deals.count && !data.leads.open && !data.leads.newInPeriod && !data.series.some((s) => s.deals || s.leads);
  const series = useMemo(
    () =>
      (data?.series ?? []).map((s) => ({ key: s.month, label: monthLabel(s.month, true), total: Math.round(s.totalIlsAgorot / 100), leads: s.leads, deals: s.deals })),
    [data],
  );

  return (
    <div className="sl-page">
      <ScreenHeader
        sub={data ? `${fmtDate(data.period.from)} – ${fmtDate(data.period.to)}` : "מכירות, לידים וגבייה במבט אחד"}
        primary={allowed("sales.edit") ? { label: "עסקה חדשה", icon: "plus", onClick: () => navigate("/sales/deals?new=1") } : undefined}
        more={[
          { key: "leads", label: "לידים", icon: "phone", onClick: () => navigate("/sales/leads") },
          { key: "deals", label: "עסקאות", icon: "receipt", onClick: () => navigate("/sales/deals") },
        ]}
      />
      <div className="sl-period">
        <Segmented
          ariaLabel="תקופה"
          value={period}
          onChange={(v) => setPeriod(v as Period)}
          items={[
            { value: "30d", label: "30 יום" },
            { value: "month", label: "החודש" },
            { value: "prev", label: "חודש שעבר" },
            { value: "year", label: "השנה" },
            { value: "custom", label: "טווח…" },
          ]}
        />
        {period === "custom" ? (
          <>
            <TextInput type="date" aria-label="מתאריך" value={custom.from} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} />
            <span className="sl-muted">עד</span>
            <TextInput type="date" aria-label="עד תאריך" value={custom.to} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} />
          </>
        ) : null}
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}

      <StatGrid min={170}>
        <StatTile
          label="מכירות בתקופה"
          icon="coin"
          tone="primary"
          loading={loading}
          value={data ? fmtIls(data.deals.totalIlsAgorot) : undefined}
          sub={data ? `${fmtNum(data.deals.count)} עסקאות` : undefined}
          onClick={() => navigate(`/sales/deals?from=${range.from}&to=${range.to}`)}
        />
        <StatTile label="לקוחות שקנו" icon="users" tone="success" loading={loading} value={data ? fmtNum(data.deals.customers) : undefined} />
        <StatTile label="לידים חדשים" icon="plus" tone="info" loading={loading} value={data ? fmtNum(data.leads.newInPeriod) : undefined} sub={data ? `${fmtNum(data.leads.open)} פתוחים` : undefined} onClick={() => navigate("/sales/leads")} />
        <StatTile label="אחוז המרה" icon="trend" tone="violet" loading={loading} value={data ? `${Math.round(data.leads.conversionPct)}%` : undefined} sub={data ? `${fmtNum(data.leads.wonInPeriod)} נסגרו` : undefined} />
        <StatTile label="שיחות להיום" icon="phone" tone="primary" loading={loading} value={data ? fmtNum(data.leads.dueToday) : undefined} onClick={() => navigate("/sales/leads?due=today")} />
        <StatTile label="באיחור" icon="clock" tone="danger" warn={Boolean(data?.leads.overdue)} loading={loading} value={data ? fmtNum(data.leads.overdue) : undefined} onClick={() => navigate("/sales/leads?due=overdue")} />
        <StatTile
          label="יתרות פתוחות"
          icon="wallet"
          tone="warning"
          loading={loading}
          value={data ? fmtIls(data.balances.balanceAgorot) : undefined}
          sub={data ? `${fmtNum(data.balances.deals)} עסקאות` : undefined}
          onClick={() => navigate("/sales/collections")}
        />
        <StatTile label="הקשיבו בקו" icon="mic" tone="neutral" loading={loading} value={data ? fmtNum(data.leads.listenedInPeriod) : undefined} onClick={() => navigate("/sales/leads?listened=1")} />
      </StatGrid>

      {loading ? (
        <div className="sl-busy">
          <Spinner size={26} />
        </div>
      ) : null}

      {empty ? (
        <EmptyState
          icon="chart"
          title="עדיין אין נתוני מכירות"
          action={
            allowed("sales.edit") ? (
              <Button variant="primary" icon="plus" onClick={() => navigate("/sales/leads?new=1")}>
                ליד ראשון
              </Button>
            ) : undefined
          }
        >
          הוסיפו לידים ועסקאות — או חברו את קו ימות המשיח בהגדרות כדי שמי שמקשיב בקו ייכנס לבד ללידים.
        </EmptyState>
      ) : null}

      {data && !empty ? (
          <PanelGrid cols={2}>
            <Panel
              title="מגמה חודשית"
              sub="12 החודשים האחרונים"
              icon="chart"
              span={2}
              actions={
                <Segmented
                  ariaLabel="מה להציג"
                  value={trend}
                  onChange={(v) => setTrend(v as "sales")}
                  items={[
                    { value: "sales", label: "מכירות ₪" },
                    { value: "leads", label: "לידים" },
                  ]}
                />
              }
            >
              <BarChart
                data={series}
                height={240}
                series={trend === "sales" ? [{ key: "total", label: "מכירות", tone: "primary" }] : [{ key: "leads", label: "לידים", tone: "info" }]}
                formatValue={(v: number) => (trend === "sales" ? `₪${fmtNum(v)}` : fmtNum(v))}
              />
            </Panel>
            <Panel title="לחזור היום" icon="phone" sub="לידים שמחכים לשיחה (כולל באיחור)" flush actions={<Button size="sm" variant="ghost" onClick={() => navigate("/sales/leads?due=due")}>לכולם</Button>}>
              {data.dueLeads.length ? (
                <ul className="sl-due">
                  {data.dueLeads.map((l) => (
                    <li key={l.id}>
                      <button type="button" onClick={() => navigate(`/sales/leads/${l.id}`)}>
                        <span className="sl-cell2">
                          <b>{l.name || l.phone || "ללא שם"}</b>
                          <small>{[l.name ? l.phone : null, label("sales_lead_status", l.statusKey), label("sales_lead_outcome", l.outcomeKey)].filter(Boolean).join(" · ")}</small>
                        </span>
                        <span className={l.followUpOn && l.followUpOn < today ? "sl-danger" : "sl-muted"}>{fmtDate(l.followUpOn)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <EmptyState compact icon="check" title="אין שיחות שמחכות" />
              )}
            </Panel>
            <Panel title="עסקאות אחרונות" icon="receipt" flush actions={<Button size="sm" variant="ghost" onClick={() => navigate("/sales/deals")}>כל העסקאות</Button>}>
              <SimpleTable
                ariaLabel="עסקאות אחרונות"
                rows={data.recentDeals}
                rowKey="id"
                onRowClick={(r: RecentDeal) => navigate(`/sales/deals/${r.id}`)}
                empty="אין עסקאות"
                columns={[
                  { key: "dealOn", label: "תאריך", render: (r: RecentDeal) => fmtDate(r.dealOn) },
                  { key: "customerName", label: "לקוח", render: (r: RecentDeal) => r.customerName || "—" },
                  { key: "productName", label: "מוצר" },
                  { key: "total", label: "סכום", align: "end", render: (r: RecentDeal) => <span className="sl-num">{fmtIls(r.totalIlsAgorot)}</span> },
                ]}
              />
            </Panel>
            <Panel title="לפי מוצר" icon="box">
              <RankList
                items={data.byProduct.map((p) => ({ key: p.name, label: p.name, value: p.totalIlsAgorot, sub: `${fmtNum(p.deals)} עסקאות` }))}
                formatValue={(v: number) => fmtIls(v)}
                empty="אין מכירות בתקופה"
              />
            </Panel>
            <Panel title="לפי נציג" icon="users">
              <RankList
                tone="success"
                items={data.byRep.map((r) => ({ key: r.repId ?? "none", label: r.name || "ללא נציג", value: r.totalIlsAgorot, sub: `${fmtNum(r.deals)} עסקאות` }))}
                formatValue={(v: number) => fmtIls(v)}
                empty="אין מכירות בתקופה"
              />
            </Panel>
            <Panel title="לפי ערוץ" icon="swap">
              <Bars
                items={data.byChannel.map((c) => ({
                  key: c.channel,
                  label: CHANNEL_LABEL[c.channel] ?? c.channel,
                  value: c.totalIlsAgorot,
                  text: `${fmtIls(c.totalIlsAgorot)} · ${fmtNum(c.deals)}`,
                }))}
              />
            </Panel>
            <Panel title="מקורות לידים" icon="filter" sub="לידים בתקופה ומתוכם נסגרו">
              <Bars
                tone="success"
                items={data.bySource.map((s) => ({
                  key: s.source ?? "none",
                  label: s.source ? label("sales_lead_source", s.source) : "ללא מקור",
                  value: s.leads,
                  text: `${fmtNum(s.leads)} · ${fmtNum(s.won)} נסגרו`,
                }))}
              />
            </Panel>
          </PanelGrid>
      ) : null}
    </div>
  );
}

export default SalesHomePage;
